import { Loading } from '@revelio/core';
import { Tag } from '@revelio/ui';

import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { normalizeProfileUrl } from '../../../utils/normalizeProfileUrl';
import {
  MarkdownDescription,
  shouldRenderMarkdown,
} from '../MarkdownDescription/MarkdownDescription';
import styles from './FullPostingInfo.module.css';
import { formatSalaryRange, relativePostedAt } from './postingFormat';
import type { FullPostingInfoData } from './types';
import { useFormattedPostingDescription } from './useFormattedPostingDescription';

interface FullPostingInfoProps {
  data: FullPostingInfoData;
}

const ThemedInfoRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className={styles.row}>
    <span className={styles.rowLabel}>{label}:</span>
    <span className={styles.rowValue}>{children}</span>
  </div>
);

export const FullPostingInfo = ({ data }: FullPostingInfoProps) => {
  const { code: currencyCode } = useCurrencyPreference();
  const salaryText = data.salary
    ? formatSalaryRange(0, data.salary, currencyCode)
    : undefined;
  const relativePosted = data.postedAt ? relativePostedAt(data.postedAt) : '';
  const postedText = relativePosted ? `Posted ${relativePosted}` : undefined;
  const postingUrl = data.url ? normalizeProfileUrl(data.url) : null;
  const {
    description: formattedDescription,
    isFormatted,
    isFormatting,
  } = useFormattedPostingDescription(data.description ?? '');

  return (
    <div className={styles.container} data-testid="full-posting-info">
      <div className={styles.header}>
        <p className={styles.title}>Job details</p>
        <div className={styles.rows}>
          {salaryText && (
            <ThemedInfoRow label="Salary">
              <span className={styles.salary}>{salaryText}</span>
            </ThemedInfoRow>
          )}
          {data.jobType && (
            <ThemedInfoRow label="Job Type">{data.jobType}</ThemedInfoRow>
          )}
          {data.location && (
            <ThemedInfoRow label="Work Setting">{data.location}</ThemedInfoRow>
          )}
          {postingUrl && (
            <ThemedInfoRow label="Posting URL">
              <a
                className={styles.link}
                href={postingUrl.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="full-posting-url"
              >
                {postingUrl.displayValue}
              </a>
            </ThemedInfoRow>
          )}
          {data.benefits && data.benefits.length > 0 && (
            <ThemedInfoRow label="Benefits">
              <span className={styles.benefits}>
                {data.benefits.map((benefit, idx) => (
                  <Tag
                    key={`benefit-${idx}`}
                    size="sm"
                    colorScheme="gray"
                    variant="subtle"
                  >
                    {benefit}
                  </Tag>
                ))}
              </span>
            </ThemedInfoRow>
          )}
        </div>
      </div>

      {data.description && (
        <div className={styles.section}>
          <p className={styles.title}>Full job description</p>
          <div className={styles.description}>
            {(() => {
              if (isFormatting) {
                return (
                  <div className={styles.loading}>
                    <Loading size="md" />
                  </div>
                );
              }
              if (
                isFormatted ||
                shouldRenderMarkdown(formattedDescription, false)
              ) {
                return (
                  <MarkdownDescription description={formattedDescription} />
                );
              }
              return <p className={styles.plainText}>{formattedDescription}</p>;
            })()}
          </div>
        </div>
      )}

      {postedText && <p className={styles.postedAt}>{postedText}</p>}
    </div>
  );
};
